import { useI18n } from "@/lib/i18n";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";

export default function About() {
  const { t, lang } = useI18n();
  const nl = lang === "nl";
  return (
    <div data-testid="about-page" className="pt-28 pb-24 bg-[#F7F7F6]">
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-3">
            <p className="font-mono-label text-[11px] text-neutral-500">[ MANIFESTO / 01 ]</p>
          </div>
          <div className="md:col-span-9">
            <h1 className="font-serif-display font-light text-5xl md:text-8xl leading-[0.95] tracking-tight">
              <MaskedLine>{nl ? "Onderzoek verdient" : "Research deserves"}</MaskedLine>
              <MaskedLine i={1} className="italic text-neutral-500">{nl ? "betere materialen." : "better materials."}</MaskedLine>
            </h1>
            <FadeUp delay={0.4}>
              <p className="mt-10 max-w-2xl text-neutral-600 text-lg leading-relaxed">
                {nl
                  ? "Peptivance is opgericht door onderzoekers voor onderzoekers. We zagen te vaak dat de materialen die het lab bereikten — flacons, oplosmiddelen, gelyofiliseerde peptiden — niet voldeden aan de standaard die het onderzoek verdiende. Peptivance is ons antwoord."
                  : "Peptivance was founded by researchers for researchers. Too often, we saw the materials arriving at the bench — vials, solvents, lyophilized peptides — fall short of the standard the research deserved. Peptivance is our answer."}
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <EditorialMarquee words={["Sterility", "Purity", "Provenance", "Precision"]} speed={16} />

      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-6 relative aspect-[4/5] border border-neutral-300 overflow-hidden bg-white">
            {/* Colored aurora backdrop */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-20 -left-20 w-[300px] h-[300px]" style={{ background: "radial-gradient(circle at center, rgba(0,82,155,0.20), transparent 65%)", filter: "blur(50px)" }} />
              <div className="absolute -bottom-20 -right-20 w-[300px] h-[300px]" style={{ background: "radial-gradient(circle at center, rgba(90,42,130,0.18), transparent 65%)", filter: "blur(50px)" }} />
            </div>
            {/* Dotted grid */}
            <div
              className="absolute inset-0 pointer-events-none opacity-[0.14]"
              style={{
                backgroundImage: "radial-gradient(circle, #111 1px, transparent 1px)",
                backgroundSize: "22px 22px",
                maskImage: "radial-gradient(ellipse at center, black 40%, transparent 78%)",
                WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 78%)",
              }}
            />
            {/* 2x2 vial mosaic */}
            <div className="relative w-full h-full grid grid-cols-2 gap-4 p-8">
              {[
                { src: "/vials/bac-water.webp", g: "#00529B" },
                { src: "/vials/tirzepatide.webp", g: "#D12A2F" },
                { src: "/vials/retatrutide.webp", g: "#5A2A82" },
                { src: "/vials/ghk.webp", g: "#007A78" },
              ].map((v, i) => (
                <div key={i} className="relative flex items-center justify-center">
                  <div className="absolute inset-0" style={{ background: `radial-gradient(circle at center, ${v.g}22, transparent 55%)`, filter: "blur(20px)" }} />
                  <img
                    src={v.src}
                    alt=""
                    className="relative max-h-full max-w-full object-contain"
                    style={{ filter: `drop-shadow(0 20px 30px ${v.g}66)` }}
                  />
                </div>
              ))}
            </div>
            <div className="absolute top-4 left-4 font-mono-label text-[10px] text-neutral-500 z-20">[ FIG.02 · CATALOGUE ]</div>
            <div className="absolute bottom-4 right-4 font-mono-label text-[10px] text-neutral-500 z-20">[ PEPTIVANCE ]</div>
          </div>
          <div className="md:col-span-6 md:pl-8">
            <p className="font-mono-label text-[11px] text-neutral-500 mb-6">[ 02 · STANDARD ]</p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl leading-tight">
              {nl ? "Aseptisch, gevalideerd, transparant." : "Aseptic, validated, transparent."}
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed max-w-lg">
              {nl
                ? "Elke lot doorloopt een gedocumenteerd proces: aseptische verpakking onder ISO Class 5 laminar flow, HPLC-validatie door onafhankelijke laboratoria, en een volledige chain of custody van grondstof tot verzending."
                : "Every lot follows a documented process: aseptic packaging under ISO Class 5 laminar flow, HPLC validation by independent laboratories, and a full chain of custody from raw material to shipment."}
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 font-mono-label text-[10px] text-neutral-500">
              <div className="border-t border-neutral-300 pt-3">[ ISO 5 ] Aseptic fill</div>
              <div className="border-t border-neutral-300 pt-3">[ HPLC ] Third-party analysis</div>
              <div className="border-t border-neutral-300 pt-3">[ GMP ] Certified sources</div>
              <div className="border-t border-neutral-300 pt-3">[ COA ] On request</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black text-[#F7F7F6] py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="font-mono-label text-[11px] text-neutral-400 mb-8">[ 03 · FROM THE LAB ]</p>
          <FadeUp>
            <h2 className="font-serif-display font-light text-5xl md:text-7xl italic max-w-3xl">
              {nl
                ? "Verzonden vanuit een klein Nederlands laboratorium — met de zorg van een groot idee."
                : "Shipped with the care of a small European laboratory — powered by a large idea."}
            </h2>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
